import type { FC, ReactNode } from 'react';
import { memo } from 'react';

import styled from 'features/theme/styled';
import { DarkThemeDropdown } from 'features/ui/components/';

const COLORS = {
  primary: '#51DBCD',
  text: {
    primary: '#FFFFFF',
    secondary: 'rgba(255, 255, 255, 0.5)',
  },
};

const DIMENSIONS = {
  columnGap: '1px',
  firstColumn: '140px',
};

export interface CallItem {
  id: string | number;
  number: ReactNode;
  time: ReactNode;
}

export interface CallDropdownProps {
  items: CallItem[];
  selectedId: string | number;
  onChange: (id: string | number) => void;
}

export const CallDropdown: FC<CallDropdownProps> = memo(function CallDropdown({
  items,
  selectedId,
  onChange,
}) {
  const dropdownItems = items.map((item) => ({
    id: item.id,
    content: (
      <CallItemContent>
        <CallNumber>{item.number}</CallNumber>
        <CallTime>{item.time}</CallTime>
      </CallItemContent>
    ),
  }));

  const renderTrigger = (selectedItem: any, isOpen: boolean) => {
    const callItem = items.find(
      (item) => String(item.id) === String(selectedItem.id)
    );
    if (!callItem) {
      return null;
    }

    return (
      <CallTriggerContent>
        <CallTriggerNumber $open={isOpen}>{callItem.number}</CallTriggerNumber>
        <CallTriggerTime $open={isOpen}>{callItem.time}</CallTriggerTime>
      </CallTriggerContent>
    );
  };

  const renderItem = (item: any, isSelected: boolean) => {
    const callItem = items.find((call) => String(call.id) === String(item.id));
    if (!callItem) {
      return null;
    }

    return (
      <CallItemContent>
        <CallItemNumber $selected={isSelected}>
          {callItem.number}
        </CallItemNumber>
        <CallItemTime $selected={isSelected}>{callItem.time}</CallItemTime>
      </CallItemContent>
    );
  };

  return (
    <DarkThemeDropdown
      items={dropdownItems}
      selectedId={selectedId}
      onChange={onChange}
      renderTrigger={renderTrigger}
      renderItem={renderItem}
    />
  );
});

const CallItemContent = styled('div')`
  display: grid;
  grid-template-columns: ${DIMENSIONS.firstColumn} 1fr;
  column-gap: ${DIMENSIONS.columnGap};
  align-items: center;
  width: 100%;
`;

const CallTriggerContent = styled('div')`
  display: grid;
  grid-template-columns: ${DIMENSIONS.firstColumn} 1fr;
  column-gap: ${DIMENSIONS.columnGap};
  align-items: center;
  width: 100%;
`;

const CallTriggerNumber = styled('span')<{ $open: boolean }>`
  color: ${({ $open }) => ($open ? COLORS.primary : COLORS.text.primary)};
  white-space: nowrap;
`;

const CallTriggerTime = styled('span')<{ $open: boolean }>`
  color: ${({ $open }) => ($open ? COLORS.primary : COLORS.text.secondary)};
  white-space: nowrap;
  text-align: right;
`;

const CallNumber = styled('span')`
  color: ${COLORS.text.primary};
  white-space: nowrap;
`;

const CallTime = styled('span')`
  color: ${COLORS.text.secondary};
  white-space: nowrap;
  text-align: right;
`;

const CallItemNumber = styled('span')<{ $selected: boolean }>`
  color: ${({ $selected }) =>
    $selected ? COLORS.primary : COLORS.text.primary};
  white-space: nowrap;
`;

const CallItemTime = styled('span')<{ $selected: boolean }>`
  color: ${({ $selected }) =>
    $selected ? COLORS.primary : COLORS.text.secondary};
  white-space: nowrap;
  text-align: right;
`;
